import type {
  AuthResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreateChapterRequest,
  CreateProjectRequest,
  PatchProjectRequest,
  ProjectSettings,
  PatchProjectSettingsRequest,
  Chapter,
  Project,
  ProjectVersion,
  Region,
  StepWithRegions,
  Asset,
  CreateInviteRequest,
  CreateInviteResponse,
  Enrollment,
  PendingUser,
  ChapterShareState,
  ChapterShareMode,
  ProjectVersionStatus,
} from '@simultrain/shared';
import { useAuth } from '../auth/store.js';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

class ApiError extends Error {
  constructor(public status: number, public data: unknown) {
    super(`API ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}, isRetry = false): Promise<T> {
  const { accessToken, refreshToken, setTokens, logout } = useAuth.getState();
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (res.status === 401 && !isRetry && refreshToken) {
    // Try a single refresh round-trip.
    const refreshed = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (refreshed.ok) {
      const tokens = await refreshed.json();
      setTokens(tokens.accessToken, tokens.refreshToken);
      return request<T>(path, init, true);
    }
    logout();
    throw new ApiError(401, await safeJson(res));
  }

  if (!res.ok) {
    throw new ApiError(res.status, await safeJson(res));
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export const api = {
  // auth
  login: (email: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (email: string, password: string, displayName: string, locale: 'nl' | 'en' = 'nl') =>
    request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName, locale }),
    }),
  me: () => request('/auth/me'),
  logoutCall: (refreshToken: string) =>
    request('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  changePassword: (body: ChangePasswordRequest) =>
    request<ChangePasswordResponse>('/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),

  // projects
  listProjects: () => request<Project[]>('/projects'),
  createProject: (body: CreateProjectRequest) =>
    request<{ project: Project; initialVersion: ProjectVersion }>('/projects', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getProject: (id: string) =>
    request<Project & { versions: ProjectVersion[] }>(`/projects/${id}`),
  patchProject: (id: string, body: PatchProjectRequest) =>
    request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProject: (id: string) => request(`/projects/${id}`, { method: 'DELETE' }),
  patchVersionStatus: (versionId: string, status: ProjectVersionStatus) =>
    request<ProjectVersion>(`/versions/${versionId}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  getProjectSettings: (id: string) => request<ProjectSettings>(`/projects/${id}/settings`),
  patchProjectSettings: (id: string, body: PatchProjectSettingsRequest) =>
    request<ProjectSettings>(`/projects/${id}/settings`, { method: 'PATCH', body: JSON.stringify(body) }),
  getPlatformSettings: () => request<ProjectSettings>('/platform/settings'),
  patchPlatformSettings: (body: PatchProjectSettingsRequest) =>
    request<ProjectSettings>('/platform/settings', { method: 'PATCH', body: JSON.stringify(body) }),

  // chapters
  listChapters: (versionId: string) =>
    request<Chapter[]>(`/versions/${versionId}/chapters`),
  createChapter: (versionId: string, body: CreateChapterRequest) =>
    request<Chapter>(`/versions/${versionId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reorderChapters: (versionId: string, orderedIds: string[]) =>
    request<Chapter[]>(`/versions/${versionId}/chapters/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ orderedIds }),
    }),
  getChapterFull: (id: string) =>
    request<{
      chapter: Chapter;
      steps: StepWithRegions[];
      ttsAudio: Record<string, string>;
      project: { id: string; name: string };
    }>(`/chapters/${id}/full`),
  patchChapter: (id: string, body: Partial<Chapter>) =>
    request<Chapter>(`/chapters/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteChapter: (id: string) => request(`/chapters/${id}`, { method: 'DELETE' }),
  createChapterDraft: (id: string) => request<Chapter>(`/chapters/${id}/draft`, { method: 'POST' }),
  patchChapterDraftStatus: (id: string, status: 'draft' | 'review') =>
    request<Chapter>(`/chapters/${id}/draft`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  publishChapterDraft: (id: string) => request<Chapter>(`/chapters/${id}/draft/publish`, { method: 'POST' }),
  deleteChapterDraft: (id: string) => request(`/chapters/${id}/draft`, { method: 'DELETE' }),
  getChapterShare: (id: string) => request<ChapterShareState>(`/chapters/${id}/share`),
  createChapterShare: (id: string, mode: ChapterShareMode) =>
    request<ChapterShareState>(`/chapters/${id}/share`, { method: 'POST', body: JSON.stringify({ mode }) }),
  patchChapterShareMode: (id: string, mode: ChapterShareMode) =>
    request<ChapterShareState>(`/chapters/${id}/share`, { method: 'PATCH', body: JSON.stringify({ mode }) }),
  rotateChapterShare: (id: string) =>
    request<ChapterShareState>(`/chapters/${id}/share/rotate`, { method: 'POST' }),
  deleteChapterShare: (id: string) => request(`/chapters/${id}/share`, { method: 'DELETE' }),
  generateTts: (chapterId: string) =>
    request<{ generated: number; cached: number; total: number }>(`/chapters/${chapterId}/tts/generate`, {
      method: 'POST',
    }),

  // steps
  createStep: (chapterId: string, body: Partial<StepWithRegions>) =>
    request<StepWithRegions>(`/chapters/${chapterId}/steps`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchStep: (id: string, body: Partial<StepWithRegions> & { regions?: Region[] }) =>
    request<StepWithRegions>(`/steps/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteStep: (id: string) => request(`/steps/${id}`, { method: 'DELETE' }),

  // assets
  uploadAsset: async (versionId: string, file: File | Blob, filename = 'screenshot.png') => {
    const fd = new FormData();
    fd.append('file', file, filename);
    return request<Asset>(`/versions/${versionId}/assets`, {
      method: 'POST',
      body: fd,
    });
  },
  assetUrl: (id: string) => `${API_URL}/assets/${id}`,
  // Reuses a screenshot from elsewhere (own projects only) by copying it into
  // this version's own storage — see the route for why it's a copy, not a
  // shared reference.
  copyAsset: (targetVersionId: string, sourceAssetId: string) =>
    request<Asset>(`/versions/${targetVersionId}/assets/${sourceAssetId}/copy`, { method: 'POST' }),

  // enrollment / invites (per project)
  listEnrollments: (projectId: string) => request<Enrollment[]>(`/projects/${projectId}/enrollments`),
  createInvite: (projectId: string, body: CreateInviteRequest) =>
    request<CreateInviteResponse>(`/projects/${projectId}/invites`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteEnrollment: (projectId: string, userId: string) =>
    request(`/projects/${projectId}/enrollments/${userId}`, { method: 'DELETE' }),

  // pending self-registrations (training_admin/super_admin)
  listPendingUsers: () => request<PendingUser[]>('/admin/pending-users'),
  approvePendingUser: (id: string) => request(`/admin/pending-users/${id}/approve`, { method: 'POST' }),
  rejectPendingUser: (id: string) => request(`/admin/pending-users/${id}/reject`, { method: 'POST' }),
};

export { ApiError };
export const API_BASE_URL = API_URL;
